/**
 * EDMS Dashboard – folder tree (left) via react-complex-tree, folder contents (right).
 * Context menu on folders: New subfolder, Rename, Delete. Context menu on documents: Rename, Delete.
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Folder, FolderOpen, FileText, FolderPlus, Pencil, Trash2, Upload, ExternalLink, X, Eye, Download } from 'lucide-react';
import {
  UncontrolledTreeEnvironment,
  Tree,
  StaticTreeDataProvider,
} from 'react-complex-tree';
import 'react-complex-tree/lib/style-modern.css';
import PageHeader from '../../components/shared/PageHeader';
import Modal from '../../components/shared/Modal';
import ModalFormActions from '../../components/shared/ModalFormActions';
import ConfirmDialog from '../../components/shared/ConfirmDialog';
import SuccessDialog from '../../components/shared/SuccessDialog';
import Pagination from '../../components/shared/Pagination';
import { useActiveRepositories } from '../../hooks/edms/useActiveRepositories';
import { useActiveFolderTypes } from '../../hooks/edms/useActiveFolderTypes';
import { useFolderTree } from '../../hooks/edms/useFolderTree';
import { useFolderContents } from '../../hooks/edms/useFolderContents';
import { getActiveDocumentTypes } from '../../api/edms/lookups';
import {
  createFolder,
  updateFolder,
  deleteFolder,
  getFolderById,
  searchFolders,
  getCategoryRootFolder,
  ensureCategoryRootAndGetParentId,
} from '../../api/edms/folders';
import { uploadDocument, updateDocument, deleteDocument, getDocumentUrl, getDocumentDownload } from '../../api/edms/documents';

const APP_ID = 'documents-management';
const APP_NAME = 'Documents Management';
const MODULE = 'Archive';
const TREE_ID = 'edms-folder-tree';

const GROUP_CATEGORIES = [
  { value: '', label: 'All' },
  { value: 'Confidential', label: 'Confidential' },
  { value: 'General', label: 'General' },
  { value: 'Personal', label: 'Personal' },
];

const PERSONAL_SUBCATEGORIES = [
  { value: '', label: 'Select…' },
  { value: 'Staff', label: 'Staff' },
  { value: 'Member', label: 'Member' },
];

/** Floating context menu at (x, y) with action buttons */
function ContextMenu({ x, y, items, onClose }) {
  const ref = React.useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  if (!items?.length) return null;

  return (
    <div
      ref={ref}
      className="fixed z-50 min-w-[180px] rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
      style={{ left: x, top: y }}
    >
      {items.map(({ key, label, icon: Icon, onClick }) => (
        <button
          key={key}
          type="button"
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100"
          onClick={() => {
            onClick?.();
            onClose?.();
          }}
        >
          {Icon && <Icon className="h-4 w-4 shrink-0" />}
          {label}
        </button>
      ))}
    </div>
  );
}

/**
 * Convert backend folder tree (array of { _id, name, children? }) into
 * react-complex-tree items: { [id]: { index, data, children, isFolder } }.
 * Adds a virtual root so the tree has a single root.
 */
function buildTreeItems(nodes) {
  const items = {};
  const rootChildren = [];

  function process(node, addToSiblings) {
    const id = String(node._id ?? node.id ?? '');
    if (!id) return null;
    const children = node.children ?? node.subfolders ?? [];
    const childIds = [];
    children.forEach((c) => process(c, childIds));
    items[id] = {
      index: id,
      data: {
        name: node.folderName ?? node.name ?? node.label ?? 'Unnamed',
        _id: id,
        ...(node.repository != null && { repository: node.repository }),
        ...(node.groupCategory != null && { groupCategory: node.groupCategory }),
        ...(node.groupSubCategory != null && { groupSubCategory: node.groupSubCategory }),
      },
      children: childIds,
      isFolder: true,
    };
    if (addToSiblings) addToSiblings.push(id);
    return id;
  }

  if (nodes?.length) {
    nodes.forEach((node) => process(node, rootChildren));
  }

  items.root = {
    index: 'root',
    data: { name: 'Folders' },
    children: rootChildren,
    isFolder: true,
  };

  return items;
}

/**
 * Filter tree items by search query: keep nodes whose name matches (case-insensitive)
 * or that have a matching descendant. Preserves hierarchy for matches.
 */
function filterTreeItemsByQuery(items, query, getItemTitle) {
  if (!items || !query || typeof query !== 'string') return items;
  const q = query.trim().toLowerCase();
  if (!q) return items;

  const getTitle = getItemTitle || ((item) => item?.data?.name ?? item?.data ?? '');
  const result = { ...items };

  function getTitleString(node) {
    const raw = getTitle(node);
    if (typeof raw === 'string') return raw;
    if (raw != null && typeof raw === 'object') return raw.name ?? raw.label ?? '';
    return '';
  }

  function filterChildren(nodeId) {
    const node = result[nodeId];
    if (!node) return [];
    const title = getTitleString(node);
    const matches = title.length > 0 && title.toLowerCase().includes(q);
    const childIds = node.children || [];
    const keptChildren = childIds.flatMap(filterChildren);
    if (matches || keptChildren.length > 0) {
      result[nodeId] = { ...node, children: keptChildren };
      return [nodeId];
    }
    return [];
  }

  const root = result.root;
  if (!root) return items;
  const rootChildIds = root.children || [];
  const keptRootChildren = rootChildIds.flatMap(filterChildren);
  result.root = { ...root, children: keptRootChildren };
  return result;
}

const EdmsDashboard = () => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const [selectedFolderId, setSelectedFolderId] = useState(
    () => location.state?.selectedFolderId ?? null
  );
  const [treeFilters, setTreeFilters] = useState(() => ({
    repository: location.state?.repository ?? '',
    groupCategory: location.state?.groupCategory ?? '',
    groupSubCategory: '',
  }));
  const [treeSearchQuery, setTreeSearchQuery] = useState('');

  const [folderContextMenu, setFolderContextMenu] = useState(null);
  const [docContextMenu, setDocContextMenu] = useState(null);
  const [subfolderModal, setSubfolderModal] = useState(null);
  const [renameFolderModal, setRenameFolderModal] = useState(null);
  const [renameDocModal, setRenameDocModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [uploadModal, setUploadModal] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDocumentTypeId, setUploadDocumentTypeId] = useState('');
  const [documentViewer, setDocumentViewer] = useState(null);
  const [documentsPage, setDocumentsPage] = useState(1);
  const documentsLimit = 10;
  const [dropZoneDragOver, setDropZoneDragOver] = useState(false);
  const [dropZoneUploading, setDropZoneUploading] = useState(false);

  const [newFolderForm, setNewFolderForm] = useState({
    folderName: '',
    folderNumber: '',
    groupCategory: '',
    groupSubCategory: '',
    folderTypeId: '',
  });
  const [newFolderModal, setNewFolderModal] = useState(false);
  const [newFolderFormNew, setNewFolderFormNew] = useState({
    folderName: '',
    folderNumber: '',
    repository: '',
    groupCategory: '',
    groupSubCategory: '',
    folderTypeId: '',
    parentFolderId: '',
  });
  const [rootFolderModal, setRootFolderModal] = useState(false);
  const [rootFolderForm, setRootFolderForm] = useState({
    folderName: '',
    folderNumber: '',
    repository: '',
    groupCategory: '',
    groupSubCategory: '',
    folderTypeId: '',
  });
  const [createFolderValidationError, setCreateFolderValidationError] = useState('');
  const [folderCreatedSuccess, setFolderCreatedSuccess] = useState({ isOpen: false, message: '' });
  const [documentUploadedSuccess, setDocumentUploadedSuccess] = useState({ isOpen: false, message: '' });

  const { repositories } = useActiveRepositories({ enabled: true });
  const { folderTypes } = useActiveFolderTypes({ enabled: true });

  const parentFolderSearchParamsNew =
    newFolderModal && newFolderFormNew.repository && newFolderFormNew.groupCategory
      ? {
          repository: newFolderFormNew.repository,
          groupCategory: newFolderFormNew.groupCategory,
          folderTypeId: newFolderFormNew.folderTypeId || undefined,
          page: 1,
          limit: 500,
        }
      : null;
  const { data: parentFoldersDataNew } = useQuery({
    queryKey: ['edms', 'folders', 'parentList', parentFolderSearchParamsNew],
    queryFn: () => searchFolders(parentFolderSearchParamsNew || {}),
    enabled: !!parentFolderSearchParamsNew,
  });
  const parentFoldersListNew = Array.isArray(parentFoldersDataNew?.data)
    ? parentFoldersDataNew.data
    : Array.isArray(parentFoldersDataNew)
    ? parentFoldersDataNew
    : parentFoldersDataNew?.folders ?? [];

  const { data: documentTypesData } = useQuery({
    queryKey: ['edms', 'documentTypes', 'active'],
    queryFn: getActiveDocumentTypes,
    enabled: !!uploadModal,
  });
  const documentTypes = useMemo(() => {
    const d = documentTypesData?.data ?? documentTypesData;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.documentTypes)) return d.documentTypes;
    return [];
  }, [documentTypesData]);

  const { data: documentViewUrlData } = useQuery({
    queryKey: ['documentViewUrl', documentViewer?.id],
    queryFn: () => getDocumentUrl(documentViewer.id),
    enabled: !!documentViewer?.id,
    retry: false,
    staleTime: 60 * 1000,
  });
  const documentViewUrl = useMemo(() => {
    if (!documentViewer?.id) return null;
    const d = documentViewUrlData;
    const raw =
      (typeof d === 'string' && d) ? d
      : d?.url ?? d?.viewUrl ?? d?.fileUrl
      ?? (typeof d?.data === 'string' ? d.data : null)
      ?? d?.data?.url ?? d?.data?.viewUrl
      ?? null;
    if (typeof raw === 'string' && raw.trim()) {
      const url = raw.trim();
      if (url.startsWith('http://') || url.startsWith('https://')) return url;
      return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
    }
    const base =
      (typeof process !== 'undefined' && process.env?.REACT_APP_API_BASE_URL) ||
      (typeof window !== 'undefined' && window.location?.origin) ||
      '';
    const path = `/api/documents/${documentViewer.id}/view`;
    return base ? `${base.replace(/\/$/, '')}${path}` : path;
  }, [documentViewer?.id, documentViewUrlData]);

  const createFolderMutation = useMutation({
    mutationFn: createFolder,
    onSuccess: async () => {
      setSubfolderModal(null);
      setNewFolderModal(false);
      setRootFolderModal(false);
      setCreateFolderValidationError('');
      setNewFolderForm({ folderName: '', folderNumber: '', groupCategory: '', groupSubCategory: '', folderTypeId: '' });
      setNewFolderFormNew({ folderName: '', folderNumber: '', repository: '', groupCategory: '', groupSubCategory: '', folderTypeId: '', parentFolderId: '' });
      setRootFolderForm({ folderName: '', folderNumber: '', repository: '', groupCategory: '', groupSubCategory: '', folderTypeId: '' });
      await queryClient.refetchQueries({ queryKey: ['folderTree'] });
      setFolderCreatedSuccess({ isOpen: true, message: 'Folder created successfully.' });
    },
    onError: () => {
      setCreateFolderValidationError('');
    },
  });

  const resetNewFolderFormNew = useCallback(() => {
    setCreateFolderValidationError('');
    setNewFolderFormNew({ folderName: '', folderNumber: '', repository: '', groupCategory: '', groupSubCategory: '', folderTypeId: '', parentFolderId: '' });
  }, []);
  const updateFolderMutation = useMutation({
    mutationFn: ({ id, payload }) => updateFolder(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folderTree'] });
      setRenameFolderModal(null);
      setRenameValue('');
    },
  });
  const deleteFolderMutation = useMutation({
    mutationFn: deleteFolder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folderTree'] });
      setSelectedFolderId((id) => (id === deleteConfirm?.id ? null : id));
      setDeleteConfirm(null);
    },
  });
  const updateDocMutation = useMutation({
    mutationFn: ({ id, payload }) => updateDocument(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folderContents'] });
      setRenameDocModal(null);
      setRenameValue('');
    },
  });
  const deleteDocMutation = useMutation({
    mutationFn: deleteDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folderContents'] });
      setDocContextMenu(null);
      setDeleteConfirm(null);
    },
  });
  const uploadDocMutation = useMutation({
    mutationFn: uploadDocument,
    onSuccess: async () => {
      setUploadModal(null);
      setUploadFile(null);
      setUploadTitle('');
      setUploadDocumentTypeId('');
      setDocumentUploadedSuccess({ isOpen: true, message: 'Document uploaded successfully.' });
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['folderContents'] }),
        queryClient.refetchQueries({ queryKey: ['folderTree'] }),
      ]);
    },
  });

  const filtersForApi = useMemo(() => {
    const f = {};
    if (treeFilters.repository) f.repository = treeFilters.repository;
    if (treeFilters.groupCategory) f.groupCategory = treeFilters.groupCategory;
    if (treeFilters.groupSubCategory?.trim()) f.groupSubCategory = treeFilters.groupSubCategory.trim();
    return f;
  }, [treeFilters.repository, treeFilters.groupCategory, treeFilters.groupSubCategory]);

  const { folders, isLoading: treeLoading, isError: treeError, error: treeErrorDetail, dataUpdatedAt: treeDataUpdatedAt } = useFolderTree(
    filtersForApi,
    { enabled: true }
  );
  const { folder: selectedFolderFromContents, folders: contentFolders, items, pagination: contentsPagination, isLoading: contentsLoading } = useFolderContents(
    selectedFolderId,
    { enabled: !!selectedFolderId, page: documentsPage, limit: documentsLimit }
  );

  useEffect(() => {
    setDocumentsPage(1);
  }, [selectedFolderId]);
  const { data: folderDetailsData } = useQuery({
    queryKey: ['folderDetails', selectedFolderId],
    queryFn: () => getFolderById(selectedFolderId),
    enabled: !!selectedFolderId,
  });
  const folderDetails = folderDetailsData?.data ?? folderDetailsData ?? selectedFolderFromContents;

  const treeItems = useMemo(() => buildTreeItems(folders), [folders]);
  const getItemTitle = useCallback((item) => item?.data?.name ?? item?.data ?? 'Folder', []);
  const treeItemsToShow = useMemo(
    () => filterTreeItemsByQuery(treeItems, treeSearchQuery, getItemTitle),
    [treeItems, treeSearchQuery, getItemTitle]
  );
  const dataProvider = useMemo(
    () =>
      new StaticTreeDataProvider(treeItemsToShow, (item, data) => ({
        ...item,
        data: data ?? item.data,
      })),
    [treeItemsToShow]
  );

  const handleSelectItems = (newSelectedItems) => {
    const ids = newSelectedItems[TREE_ID];
    if (ids?.length) {
      const id = ids[0];
      if (id && id !== 'root') setSelectedFolderId(id);
    }
  };

  const handleFocusItem = useCallback((item) => {
    if (item?.index && item.index !== 'root') setSelectedFolderId(item.index);
  }, []);

  const repositoryForCreate = treeFilters.repository || repositories?.[0]?.name || '';

  const resetNewFolderForm = useCallback(() => {
    setNewFolderForm({ folderName: '', folderNumber: '', groupCategory: '', groupSubCategory: '', folderTypeId: '' });
  }, []);

  const handleFolderContextMenu = useCallback((e, item) => {
    e.preventDefault();
    e.stopPropagation();
    if (item.index === 'root') return;
    setFolderContextMenu({
      x: e.clientX,
      y: e.clientY,
      item,
      title: item?.data?.name ?? 'Folder',
    });
  }, []);

  const { repositoryIds, parentIdByItemId } = useMemo(() => {
    const root = treeItems?.root;
    const repoIds = root && Array.isArray(root.children) ? root.children : [];
    const parentMap = {};
    if (treeItems) {
      Object.keys(treeItems).forEach((id) => {
        const item = treeItems[id];
        const childIds = item?.children ?? [];
        childIds.forEach((childId) => {
          parentMap[childId] = id;
        });
      });
    }
    return { repositoryIds: repoIds, parentIdByItemId: parentMap };
  }, [treeItems]);

  const canUploadToSelectedFolder = selectedFolderId && !repositoryIds?.includes(selectedFolderId);
  const handleDropZoneDrop = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDropZoneDragOver(false);
      const files = e.dataTransfer?.files;
      if (!files?.length || !selectedFolderId || !canUploadToSelectedFolder) return;
      setDropZoneUploading(true);
      try {
        for (let i = 0; i < files.length; i++) {
          const formData = new FormData();
          formData.append('file', files[i]);
          formData.append('folderId', selectedFolderId);
          await uploadDocMutation.mutateAsync(formData);
        }
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ['folderContents'] }),
          queryClient.refetchQueries({ queryKey: ['folderTree'] }),
        ]);
      } finally {
        setDropZoneUploading(false);
      }
    },
    [selectedFolderId, canUploadToSelectedFolder, uploadDocMutation, queryClient]
  );
  const handleDropZoneDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDropZoneDragOver(true);
  }, []);
  const handleDropZoneDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDropZoneDragOver(false);
  }, []);

  /** Expand tree: when searching, expand all filtered nodes so results are visible; otherwise expand to selected folder. */
  const expandedItems = useMemo(() => {
    const hasSearch = treeSearchQuery != null && String(treeSearchQuery).trim() !== '';
    if (hasSearch && treeItemsToShow) {
      const ids = ['root'];
      Object.keys(treeItemsToShow).forEach((id) => {
        if (id !== 'root' && (treeItemsToShow[id]?.children?.length ?? 0) > 0) ids.push(id);
      });
      return ids;
    }
    if (!selectedFolderId || selectedFolderId === 'root') return ['root'];
    const ancestors = [];
    let current = parentIdByItemId[selectedFolderId];
    while (current) {
      if (current !== 'root') ancestors.unshift(current);
      current = parentIdByItemId[current];
    }
    return ['root', ...ancestors, selectedFolderId];
  }, [treeSearchQuery, treeItemsToShow, selectedFolderId, parentIdByItemId]);

  /** Upload only for subfolders: not repositories, not their immediate children (e.g. Confidential, General, Personal). */
  const canUploadToFolder = folderContextMenu
    ? (() => {
        const id = folderContextMenu.item.index;
        const parentId = parentIdByItemId[id];
        if (repositoryIds.includes(id)) return false;
        if (parentId == null || parentId === 'root') return false;
        if (repositoryIds.includes(parentId)) return false;
        return true;
      })()
    : false;

  const folderContextMenuItems = folderContextMenu
    ? [
        {
          key: 'new-subfolder',
          label: 'New subfolder',
          icon: FolderPlus,
          onClick: () => {
            const parentData = folderContextMenu.item?.data ?? {};
            const parentRepo = parentData.repository ?? treeFilters.repository ?? '';
            const parentCategory = parentData.groupCategory ?? treeFilters.groupCategory ?? '';
            const parentSubCategory = parentData.groupSubCategory ?? treeFilters.groupSubCategory ?? '';
            setSubfolderModal({
              parentFolderId: folderContextMenu.item.index,
              parentTitle: folderContextMenu.title,
              repository: parentRepo || undefined,
              groupCategory: parentCategory || undefined,
              groupSubCategory: parentSubCategory || undefined,
            });
            setNewFolderForm((f) => ({
              ...f,
              folderName: '',
              folderNumber: '',
              groupCategory: parentCategory || '',
              groupSubCategory: parentSubCategory || '',
              folderTypeId: f.folderTypeId ?? '',
            }));
          },
        },
        ...(canUploadToFolder
          ? [
              {
                key: 'upload-document',
                label: 'Upload document',
                icon: Upload,
                onClick: () => {
                  setUploadModal({
                    folderId: folderContextMenu.item.index,
                    folderName: folderContextMenu.title,
                  });
                  setUploadFile(null);
                  setUploadTitle('');
                  setUploadDocumentTypeId('');
                },
              },
            ]
          : []),
        {
          key: 'rename',
          label: 'Rename',
          icon: Pencil,
          onClick: () => {
            setRenameFolderModal({
              id: folderContextMenu.item.index,
              currentName: folderContextMenu.title,
            });
            setRenameValue(folderContextMenu.title);
          },
        },
        {
          key: 'delete',
          label: 'Delete',
          icon: Trash2,
          onClick: () => {
            setDeleteConfirm({
              type: 'folder',
              id: folderContextMenu.item.index,
              name: folderContextMenu.title,
            });
          },
        },
      ]
    : [];

  const handleDocContextMenu = useCallback((e, doc) => {
    e.preventDefault();
    setDocContextMenu({
      x: e.clientX,
      y: e.clientY,
      doc,
    });
  }, []);

  const docContextMenuItems = docContextMenu
    ? [
        {
          key: 'view-doc',
          label: 'View',
          icon: FileText,
          onClick: () => {
            const doc = docContextMenu.doc;
            setDocumentViewer({
              id: doc._id ?? doc.id,
              name: doc?.title ?? doc?.name ?? doc?.fileName ?? doc?.originalName ?? 'Document',
              mimetype: doc?.mimetype ?? doc?.mimeType ?? doc?.contentType,
            });
          },
        },
        {
          key: 'rename-doc',
          label: 'Rename',
          icon: Pencil,
          onClick: () => {
            const title = docContextMenu.doc?.title ?? docContextMenu.doc?.name ?? docContextMenu.doc?.originalName ?? '';
            setRenameDocModal({ id: docContextMenu.doc._id ?? docContextMenu.doc.id, doc: docContextMenu.doc });
            setRenameValue(title);
          },
        },
        {
          key: 'delete-doc',
          label: 'Delete',
          icon: Trash2,
          onClick: () => {
            setDeleteConfirm({
              type: 'document',
              id: docContextMenu.doc._id ?? docContextMenu.doc.id,
              name: docContextMenu.doc?.title ?? docContextMenu.doc?.name ?? docContextMenu.doc?.originalName ?? 'document',
            });
          },
        },
      ]
    : [];

  const handleConfirmDelete = () => {
    if (!deleteConfirm) return;
    if (deleteConfirm.type === 'folder') {
      deleteFolderMutation.mutate(deleteConfirm.id);
    } else {
      deleteDocMutation.mutate(deleteConfirm.id);
    }
  };

  const handleDownloadDocument = useCallback(async (doc) => {
    const docId = doc._id ?? doc.id;
    if (!docId) return;
    try {
      const response = await getDocumentDownload(docId);
      const blob = response?.data ?? response;
      if (!blob || !(blob instanceof Blob)) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.title ?? doc.name ?? doc.fileName ?? doc.originalName ?? 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  }, []);

  return (
    <>
      <PageHeader
        title={`${APP_NAME} | ${MODULE}`}
        breadcrumbItems={[
          { label: APP_NAME, href: `/app/${APP_ID}` },
          { label: MODULE },
        ]}
      />
      {/* Top bar: filters and New folder */}
      <div className="flex flex-wrap items-end gap-4 py-3 px-1 border-b border-cream/60 bg-white">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-500">Repository</label>
          <select
            value={treeFilters.repository}
            onChange={(e) => setTreeFilters((f) => ({ ...f, repository: e.target.value }))}
            className="min-w-[140px] rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
          >
            <option value="">All</option>
            {repositories?.map((r) => (
              <option key={r._id ?? r.name} value={r.name ?? ''}>
                {r.name ?? r.pathSegment ?? r._id}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-500">Group category</label>
          <select
            value={treeFilters.groupCategory}
            onChange={(e) => setTreeFilters((f) => ({ ...f, groupCategory: e.target.value }))}
            className="min-w-[120px] rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
          >
            {GROUP_CATEGORIES.map((o) => (
              <option key={o.value || 'all'} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-500">Group sub-category</label>
          <input
            type="text"
            value={treeFilters.groupSubCategory}
            onChange={(e) => setTreeFilters((f) => ({ ...f, groupSubCategory: e.target.value }))}
            placeholder="Optional"
            className="min-w-[120px] rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-slate-700 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
          />
        </div>
        <div className="ml-auto flex items-end gap-2">
          <button
            type="button"
            onClick={() => {
              setRootFolderForm({
                folderName: '',
                folderNumber: '',
                repository: treeFilters.repository || '',
                groupCategory: treeFilters.groupCategory || '',
                groupSubCategory: treeFilters.groupSubCategory || '',
                folderTypeId: '',
              });
              setCreateFolderValidationError('');
              setRootFolderModal(true);
            }}
            title="New root folder (under repository)"
            className="inline-flex items-center gap-2 rounded-lg border border-primary bg-white px-3 py-2 text-sm font-medium text-primary hover:bg-primary/5 focus:outline-none focus:ring-2 focus:ring-primary/50"
            aria-label="New root folder"
          >
            <FolderPlus className="h-4 w-4" aria-hidden />
            New root folder
          </button>
          <button
            type="button"
            onClick={() => setNewFolderModal(true)}
            title="New folder"
            className="inline-flex items-center justify-center rounded-lg bg-primary p-2 text-[var(--color-accent-yellow)] hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-primary/50"
            aria-label="New folder"
          >
            <FolderPlus className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
      <div className="rounded-2xl border border-cream/60 bg-white shadow-sm overflow-hidden flex flex-col min-h-[calc(100vh-12rem)]">
        <div className="flex flex-1 min-h-0">
          {/* Left pane: folder tree (react-complex-tree) */}
          <aside className="w-72 shrink-0 border-r border-cream/60 flex flex-col bg-slate-50/50">
            <div className="p-2 border-b border-cream/60 space-y-2">
              <h2 className="text-sm font-semibold text-slate-800">Folders</h2>
              <div>
                <label htmlFor="tree-search" className="sr-only">Search folder tree</label>
                <input
                  id="tree-search"
                  type="text"
                  value={treeSearchQuery}
                  onChange={(e) => setTreeSearchQuery(e.target.value)}
                  placeholder="Search folders…"
                  className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-slate-700 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                  aria-label="Search folder tree"
                />
              </div>
            </div>
            <div className="flex-1 overflow-hidden flex flex-col min-h-0">
              {treeLoading && (
                <p className="text-sm text-slate-500 p-4">Loading folders…</p>
              )}
              {treeError && (
                <p className="text-sm text-red-600 p-4">
                  Failed to load folder tree.
                  {treeErrorDetail?.response?.data?.message && (
                    <span className="block mt-1 text-xs">{treeErrorDetail.response.data.message}</span>
                  )}
                </p>
              )}
              {!treeLoading && !treeError && !folders?.length && (
                <p className="text-sm text-slate-500 p-4">No folders.</p>
              )}
              {!treeLoading && !treeError && folders?.length > 0 && (
                <div className="flex-1 min-h-0 rct-tree-root" key={`${treeDataUpdatedAt ?? 0}-${String(treeSearchQuery || '').trim()}`}>
                  <UncontrolledTreeEnvironment
                    dataProvider={dataProvider}
                    getItemTitle={(item) => item?.data?.name ?? item?.data ?? 'Folder'}
                    viewState={{
                      [TREE_ID]: {
                        selectedItems: selectedFolderId ? [selectedFolderId] : [],
                        expandedItems,
                      },
                    }}
                    onSelectItems={handleSelectItems}
                    onFocusItem={handleFocusItem}
                    canSearchByStartingTyping={true}
                    defaultInteractionMode="click-item-to-expand"
                  >
                    <Tree
                      treeId={TREE_ID}
                      rootItem="root"
                      treeLabel="Folders"
                      renderItem={({ arrow, title, children, item, context, depth }) => {
                        const level = Math.min(depth, 4);
                        const iconColors = [
                          'text-slate-600',
                          'text-blue-600',
                          'text-emerald-600',
                          'text-amber-600',
                          'text-violet-600',
                        ];
                        const iconClass = iconColors[level];
                        const FolderIcon = context.isExpanded ? FolderOpen : Folder;
                        return (
                          <div
                            {...context.itemContainerWithChildrenProps}
                            className="rct-tree-item-row flex flex-col min-w-0 min-h-0"
                            onContextMenu={(e) => item.index !== 'root' && handleFolderContextMenu(e, item)}
                          >
                            <div
                              {...context.itemContainerWithoutChildrenProps}
                              {...context.interactiveElementProps}
                              className={`flex items-center gap-4 min-w-0 shrink-0 rounded ${context.isSelected ? 'bg-amber-100 ring-1 ring-amber-400/50' : ''}`}
                              style={{ paddingLeft: depth * 20 }}
                            >
                              {arrow}
                              {item.isFolder !== false ? (
                                <FolderIcon className={`h-4 w-4 shrink-0 ${iconClass}`} aria-hidden />
                              ) : null}
                              {depth === 1 ? <span className="font-bold truncate">{title}</span> : title}
                            </div>
                            {children}
                          </div>
                        );
                      }}
                      renderItemTitle={({ title }) => (
                        <span className="truncate">{title}</span>
                      )}
                    />
                  </UncontrolledTreeEnvironment>
                </div>
              )}
            </div>
          </aside>

          {/* Right pane: folder details + contents table */}
          <main className="flex-1 min-w-0 flex flex-col bg-white">
            <div className="px-3 py-2 border-b border-cream/60">
              <h2 className="text-sm font-semibold text-slate-800">
                {selectedFolderId ? 'Folder details' : 'Select a folder'}
              </h2>
            </div>
            <div className="flex-1 overflow-y-auto px-3 py-2">
              {!selectedFolderId && (
                <p className="text-sm text-slate-500">
                  Select a folder from the tree to view its details and contents.
                </p>
              )}
              {selectedFolderId && contentsLoading && (
                <p className="text-sm text-slate-500">Loading…</p>
              )}
              {selectedFolderId && !contentsLoading && (
                <div className="space-y-3">
                  {/* Folder details */}
                  <div className="rounded-lg border border-slate-200 bg-primary px-3 py-2">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                      Selected folder
                    </h3>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-sm">
                      <div>
                        <dt className="text-yellow-500 font-bold">File name</dt>
                        <dd className="text-white font-bold mt-0.5">
                          {folderDetails?.folderName ?? folderDetails?.name ?? treeItems[selectedFolderId]?.data?.name ?? '—'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-yellow-500 font-bold">File number</dt>
                        <dd className="text-white font-bold mt-0.5">
                          {folderDetails?.folderNumber ?? folderDetails?.number ?? '—'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-yellow-500 font-bold">Category</dt>
                        <dd className="text-white font-bold mt-0.5">
                          {folderDetails?.groupCategory ?? folderDetails?.category ?? '—'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-yellow-500 font-bold">Total contents</dt>
                        <dd className="text-white font-bold mt-0.5">
                          {(contentFolders?.length ?? 0) + (items?.length ?? 0)}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  {/* Children table */}
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                      Contents
                    </h3>
                    {(contentFolders?.length > 0 || items?.length > 0) ? (
                      <>
                      <div className="rounded-lg border border-slate-200 overflow-x-auto overflow-y-visible">
                        <table className="min-w-full divide-y divide-slate-200 text-sm table-auto">
                          <colgroup>
                            <col className="w-10" />
                            <col />
                            <col />
                            <col />
                            <col />
                            <col style={{ minWidth: '10rem' }} />
                          </colgroup>
                          <thead className="bg-slate-50">
                            <tr>
                              <th scope="col" className="px-3 py-2 text-left font-semibold text-slate-700">
                                Type
                              </th>
                              <th scope="col" className="px-3 py-2 text-left font-semibold text-slate-700">
                                Name
                              </th>
                              <th scope="col" className="px-3 py-2 text-left font-semibold text-slate-700">
                                Created at
                              </th>
                              <th scope="col" className="px-3 py-2 text-left font-semibold text-slate-700">
                                Created by
                              </th>
                              <th scope="col" className="px-3 py-2 text-left font-semibold text-slate-700">
                                Size
                              </th>
                              <th scope="col" className="px-3 py-2 text-right font-semibold text-slate-700 min-w-[10rem]">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200 bg-white">
                            {contentFolders?.map((f) => {
                              const folderId = f._id ?? f.id;
                              const folderName = f.name ?? f.label ?? f.folderName ?? '—';
                              const canUploadToThisFolder = selectedFolderId && !repositoryIds.includes(selectedFolderId);
                              return (
                                <tr key={folderId} className="hover:bg-slate-50/50">
                                  <td className="px-3 py-2 text-slate-600">
                                    <button
                                      type="button"
                                      onClick={() => setSelectedFolderId(folderId)}
                                      title="Open folder"
                                      className="cursor-pointer p-0.5 rounded hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/30"
                                    >
                                      <Folder className="h-4 w-4 text-yellow-500" aria-label="Folder" />
                                    </button>
                                  </td>
                                  <td className="px-3 py-2 text-slate-800">
                                    {folderName}
                                  </td>
                                  <td className="px-3 py-2 text-slate-600">
                                    {f.createdAt ? new Date(f.createdAt).toLocaleString() : (f.created_at ? new Date(f.created_at).toLocaleString() : '—')}
                                  </td>
                                  <td className="px-3 py-2 text-slate-600">
                                    {typeof f.createdBy === 'string' ? f.createdBy : (f.createdBy?.name ?? f.created_by?.name ?? f.createdBy ?? f.created_by ?? '—')}
                                  </td>
                                  <td className="px-3 py-2 text-slate-600">—</td>
                                  <td className="px-3 py-2 text-right min-w-[10rem] whitespace-nowrap">
                                    <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                      <button
                                        type="button"
                                        title="Add subfolder"
                                        onClick={() => {
                                          const parentRepo = f.repository ?? treeItems?.[folderId]?.data?.repository ?? treeFilters.repository ?? '';
                                          const parentCategory = f.groupCategory ?? f.category ?? treeItems?.[folderId]?.data?.groupCategory ?? treeFilters.groupCategory ?? '';
                                          const parentSubCategory = f.groupSubCategory ?? f.groupSubcategory ?? treeItems?.[folderId]?.data?.groupSubCategory ?? treeFilters.groupSubCategory ?? '';
                                          setSubfolderModal({
                                            parentFolderId: folderId,
                                            parentTitle: folderName,
                                            repository: parentRepo || undefined,
                                            groupCategory: parentCategory || undefined,
                                            groupSubCategory: parentSubCategory || undefined,
                                          });
                                          setNewFolderForm((prev) => ({
                                            ...prev,
                                            folderName: '',
                                            folderNumber: '',
                                            groupCategory: parentCategory || '',
                                            groupSubCategory: parentSubCategory || '',
                                            folderTypeId: prev.folderTypeId ?? '',
                                          }));
                                        }}
                                        className="p-1.5 rounded text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                                      >
                                        <FolderPlus className="h-4 w-4" />
                                      </button>
                                      {canUploadToThisFolder && (
                                        <button
                                          type="button"
                                          title="Upload document"
                                          onClick={() => {
                                            setUploadModal({ folderId, folderName });
                                            setUploadFile(null);
                                            setUploadTitle('');
                                            setUploadDocumentTypeId('');
                                          }}
                                          className="p-1.5 rounded text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                                        >
                                          <Upload className="h-4 w-4" />
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        title="Edit"
                                        onClick={() => {
                                          setRenameFolderModal({ id: folderId, currentName: folderName });
                                          setRenameValue(folderName);
                                        }}
                                        className="p-1.5 rounded text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </button>
                                      <button
                                        type="button"
                                        title="Delete"
                                        onClick={() => setDeleteConfirm({ type: 'folder', id: folderId, name: folderName })}
                                        className="p-1.5 rounded text-slate-500 hover:bg-slate-100 hover:text-red-600"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                            {items?.map((doc) => {
                              const docId = doc._id ?? doc.id;
                              const docName = doc.title ?? doc.name ?? doc.fileName ?? doc.originalName ?? '—';
                              const openViewer = () => setDocumentViewer({
                                id: docId,
                                name: docName,
                                mimetype: doc.mimetype ?? doc.mimeType ?? doc.contentType,
                              });
                              return (
                                <tr
                                  key={docId}
                                  className="hover:bg-slate-50/50 cursor-context-menu"
                                  onContextMenu={(e) => handleDocContextMenu(e, doc)}
                                >
                                  <td
                                    className="px-3 py-2 text-slate-600 cursor-pointer hover:bg-slate-100/80"
                                    onClick={openViewer}
                                    title="View document"
                                  >
                                    <FileText className="h-4 w-4 text-yellow-500" aria-label="Document" />
                                  </td>
                                  <td
                                    className="px-3 py-2 text-slate-800 cursor-pointer hover:bg-slate-100/80 hover:text-primary"
                                    onClick={openViewer}
                                    title="View document"
                                  >
                                    {docName}
                                  </td>
                                  <td className="px-3 py-2 text-slate-600">
                                    {doc.createdAt ? new Date(doc.createdAt).toLocaleString() : (doc.created_at ? new Date(doc.created_at).toLocaleString() : '—')}
                                  </td>
                                  <td className="px-3 py-2 text-slate-600">
                                    {typeof doc.createdBy === 'string' ? doc.createdBy : (doc.createdBy?.name ?? doc.created_by?.name ?? doc.createdBy ?? doc.created_by ?? '—')}
                                  </td>
                                  <td className="px-3 py-2 text-slate-600">
                                    {doc.size != null || doc.fileSize != null || doc.length != null
                                      ? (() => {
                                          const bytes = Number(doc.size ?? doc.fileSize ?? doc.length);
                                          if (bytes === 0) return '0 B';
                                          const k = 1024;
                                          const sizes = ['B', 'KB', 'MB', 'GB'];
                                          const i = Math.floor(Math.log(bytes) / Math.log(k));
                                          return `${(bytes / Math.pow(k, i)).toFixed(i > 1 ? 2 : 0)} ${sizes[i]}`;
                                        })()
                                      : '—'}
                                  </td>
                                  <td className="px-3 py-2 text-right min-w-[10rem] whitespace-nowrap">
                                    <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                      <button
                                        type="button"
                                        title="View"
                                        onClick={openViewer}
                                        className="p-1.5 rounded text-slate-600 hover:bg-slate-100 hover:text-slate-800 shrink-0"
                                        aria-label="View document"
                                      >
                                        <Eye className="h-4 w-4" aria-hidden />
                                      </button>
                                      <button
                                        type="button"
                                        title="Download"
                                        onClick={() => handleDownloadDocument(doc)}
                                        className="p-1.5 rounded text-slate-600 hover:bg-slate-100 hover:text-slate-800 shrink-0"
                                        aria-label="Download document"
                                      >
                                        <Download className="h-4 w-4" aria-hidden />
                                      </button>
                                      <button
                                        type="button"
                                        title="Edit"
                                        onClick={() => {
                                          setRenameDocModal({ id: docId, doc });
                                          setRenameValue(doc.title ?? doc.name ?? doc.fileName ?? doc.originalName ?? '');
                                        }}
                                        className="p-1.5 rounded text-slate-600 hover:bg-slate-100 hover:text-slate-800 shrink-0"
                                        aria-label="Edit document"
                                      >
                                        <Pencil className="h-4 w-4" aria-hidden />
                                      </button>
                                      <button
                                        type="button"
                                        title="Delete"
                                        onClick={() => setDeleteConfirm({ type: 'document', id: docId, name: docName })}
                                        className="p-1.5 rounded text-slate-600 hover:bg-slate-100 hover:text-red-600 shrink-0"
                                        aria-label="Delete document"
                                      >
                                        <Trash2 className="h-4 w-4" aria-hidden />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {contentsPagination && (
                        <div className="mt-3">
                          <Pagination pagination={contentsPagination} onPageChange={(newPage) => setDocumentsPage(newPage)} />
                        </div>
                      )}
                      </>
                    ) : canUploadToSelectedFolder ? (
                      <div
                        role="region"
                        aria-label="Drop zone for uploading documents"
                        onDragOver={handleDropZoneDragOver}
                        onDragLeave={handleDropZoneDragLeave}
                        onDrop={handleDropZoneDrop}
                        className={`rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors ${
                          dropZoneDragOver
                            ? 'border-primary bg-primary/5'
                            : 'border-slate-300 bg-slate-50/50 hover:border-slate-400'
                        } ${dropZoneUploading ? 'pointer-events-none opacity-70' : ''}`}
                      >
                        <p className="text-sm text-slate-500">This folder is empty.</p>
                        {dropZoneUploading ? (
                          <p className="text-sm text-primary font-medium mt-2">Uploading…</p>
                        ) : (
                          <p className="text-sm text-slate-600 mt-2">
                            Drag and drop files here to upload to this folder.
                          </p>
                        )}
                        <Upload className="h-10 w-10 mx-auto mt-3 text-slate-400" aria-hidden />
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 py-1.5">This folder is empty.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Folder context menu */}
      <ContextMenu
        x={folderContextMenu?.x ?? 0}
        y={folderContextMenu?.y ?? 0}
        items={folderContextMenuItems}
        onClose={() => setFolderContextMenu(null)}
      />
      {/* Document context menu */}
      <ContextMenu
        x={docContextMenu?.x ?? 0}
        y={docContextMenu?.y ?? 0}
        items={docContextMenuItems}
        onClose={() => setDocContextMenu(null)}
      />

      {/* Create new folder modal (same as EdmsFolders) */}
      <Modal
        isOpen={newFolderModal}
        onClose={() => { setNewFolderModal(false); resetNewFolderFormNew(); }}
        title="Create new folder"
        size="sm"
      >
        <form
          id="new-folder-form"
          onSubmit={async (e) => {
            e.preventDefault();
            setCreateFolderValidationError('');
            if (!newFolderFormNew.folderName.trim()) {
              setCreateFolderValidationError('Folder name is required.');
              return;
            }
            if (!newFolderFormNew.repository) {
              setCreateFolderValidationError('Repository is required.');
              return;
            }
            if (!newFolderFormNew.groupCategory) {
              setCreateFolderValidationError('Group category is required.');
              return;
            }
            if (newFolderFormNew.groupCategory === 'Personal' && !newFolderFormNew.groupSubCategory) {
              setCreateFolderValidationError('Under (Personal) is required when category is Personal.');
              return;
            }
            const payload = {
              folderName: newFolderFormNew.folderName.trim(),
              folderNumber: newFolderFormNew.folderNumber.trim() || '',
              repository: newFolderFormNew.repository,
              groupCategory: newFolderFormNew.groupCategory,
              folderTypeId: newFolderFormNew.folderTypeId || '',
            };
            if (newFolderFormNew.groupCategory === 'Personal') {
              payload.groupSubCategory = newFolderFormNew.groupSubCategory.trim() || '';
            }
            if (newFolderFormNew.parentFolderId) {
              payload.parentFolderId = newFolderFormNew.parentFolderId;
            } else if (['Confidential', 'General', 'Personal'].includes(newFolderFormNew.groupCategory)) {
              const root = await getCategoryRootFolder(newFolderFormNew.repository, newFolderFormNew.groupCategory, newFolderFormNew.groupSubCategory?.trim() || undefined);
              if (root?._id) payload.parentFolderId = root._id;
            }
            createFolderMutation.mutate(payload);
          }}
        >
          {(createFolderValidationError || createFolderMutation.isError) && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {createFolderValidationError ||
                createFolderMutation.error?.response?.data?.message ||
                createFolderMutation.error?.message ||
                'Failed to create folder.'}
            </div>
          )}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Group category *</label>
              <select
                value={newFolderFormNew.groupCategory}
                onChange={(e) => setNewFolderFormNew((f) => ({ ...f, groupCategory: e.target.value, parentFolderId: '' }))}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
              >
                <option value="">Select category</option>
                {GROUP_CATEGORIES.filter((c) => c.value).map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            {newFolderFormNew.groupCategory === 'Personal' && (
              <div>
                <label className="block text-sm font-medium text-slate-700">Under (Personal) *</label>
                <select
                  value={newFolderFormNew.groupSubCategory}
                  onChange={(e) => setNewFolderFormNew((f) => ({ ...f, groupSubCategory: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                >
                  {PERSONAL_SUBCATEGORIES.map((o) => (
                    <option key={o.value || 'blank'} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700">Repository *</label>
              <select
                value={newFolderFormNew.repository}
                onChange={(e) => setNewFolderFormNew((f) => ({ ...f, repository: e.target.value, parentFolderId: '' }))}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
              >
                <option value="">Select repository</option>
                {repositories?.map((r) => (
                  <option key={r._id ?? r.name} value={r.name ?? ''}>
                    {r.name ?? r.pathSegment ?? r._id}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Folder type</label>
              <select
                value={newFolderFormNew.folderTypeId}
                onChange={(e) => setNewFolderFormNew((f) => ({ ...f, folderTypeId: e.target.value, parentFolderId: '' }))}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
              >
                <option value="">—</option>
                {folderTypes?.map((ft) => (
                  <option key={ft._id ?? ft.id} value={ft._id ?? ft.id}>
                    {ft.name ?? ft.code ?? ft._id}
                  </option>
                ))}
              </select>
            </div>
            {newFolderFormNew.repository && newFolderFormNew.groupCategory && (
              <div>
                <label className="block text-sm font-medium text-slate-700">Parent folder</label>
                <select
                  value={newFolderFormNew.parentFolderId}
                  onChange={(e) => setNewFolderFormNew((f) => ({ ...f, parentFolderId: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                >
                  <option value="">
                    — Under {newFolderFormNew.groupCategory} (root)
                  </option>
                  {parentFoldersListNew.map((pf) => (
                    <option key={pf._id ?? pf.id} value={pf._id ?? pf.id}>
                      {pf.name ?? pf.folderName ?? pf.label ?? pf.folderNumber ?? pf._id}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-slate-500">
                  Filtered by category and folder type. Empty list means create under {newFolderFormNew.groupCategory}.
                </p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700">Folder name *</label>
              <input
                type="text"
                value={newFolderFormNew.folderName}
                onChange={(e) => setNewFolderFormNew((f) => ({ ...f, folderName: e.target.value }))}
                placeholder="Enter folder name"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Folder number</label>
              <input
                type="text"
                value={newFolderFormNew.folderNumber}
                onChange={(e) => setNewFolderFormNew((f) => ({ ...f, folderNumber: e.target.value }))}
                placeholder="Optional"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
              />
            </div>
          </div>
        </form>
        <div className="mt-4 flex justify-end gap-2">
          <ModalFormActions
            formId="new-folder-form"
            onCancel={() => { setNewFolderModal(false); resetNewFolderFormNew(); }}
            isSubmitting={createFolderMutation.isPending}
            submitText="Create"
            submittingText="Creating…"
          />
        </div>
      </Modal>

      {/* Create root folder modal (folder directly under repository, no parentFolderId) */}
      <Modal
        isOpen={rootFolderModal}
        onClose={() => { setRootFolderModal(false); setCreateFolderValidationError(''); }}
        title="Create root folder"
        size="sm"
      >
        <form
          id="root-folder-form"
          onSubmit={async (e) => {
            e.preventDefault();
            setCreateFolderValidationError('');
            if (!rootFolderForm.folderName.trim()) {
              setCreateFolderValidationError('Folder name is required.');
              return;
            }
            if (!rootFolderForm.repository) {
              setCreateFolderValidationError('Repository is required.');
              return;
            }
            if (!rootFolderForm.groupCategory) {
              setCreateFolderValidationError('Group category is required.');
              return;
            }
            if (rootFolderForm.groupCategory === 'Personal' && !(rootFolderForm.groupSubCategory || '').trim()) {
              setCreateFolderValidationError('Under (Personal) is required when category is Personal.');
              return;
            }
            const payload = {
              folderName: rootFolderForm.folderName.trim(),
              folderNumber: rootFolderForm.folderNumber.trim() || '',
              repository: rootFolderForm.repository,
              groupCategory: rootFolderForm.groupCategory,
              folderTypeId: rootFolderForm.folderTypeId || '',
            };
            if (rootFolderForm.groupCategory === 'Personal') {
              payload.groupSubCategory = rootFolderForm.groupSubCategory.trim() || '';
            }
            const existingRoot = await getCategoryRootFolder(
              rootFolderForm.repository,
              rootFolderForm.groupCategory,
              rootFolderForm.groupCategory === 'Personal' ? (rootFolderForm.groupSubCategory || '').trim() || undefined : undefined
            );
            if (existingRoot?._id) {
              payload.parentFolderId = existingRoot._id;
            }
            createFolderMutation.mutate(payload);
          }}
        >
          <p className="text-sm text-slate-600 mb-3">
            Creates a folder under the repository. If a root for this category already exists, the folder is created under it.
          </p>
          {(createFolderValidationError || createFolderMutation.isError) && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {createFolderValidationError ||
                createFolderMutation.error?.response?.data?.message ||
                createFolderMutation.error?.message ||
                'Failed to create folder.'}
            </div>
          )}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Repository *</label>
              <select
                value={rootFolderForm.repository}
                onChange={(e) => setRootFolderForm((f) => ({ ...f, repository: e.target.value }))}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
              >
                <option value="">Select repository</option>
                {repositories?.map((r) => (
                  <option key={r._id ?? r.name} value={r.name ?? ''}>
                    {r.name ?? r.pathSegment ?? r._id}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Group category *</label>
              <select
                value={rootFolderForm.groupCategory}
                onChange={(e) => setRootFolderForm((f) => ({ ...f, groupCategory: e.target.value }))}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
              >
                <option value="">Select category</option>
                {GROUP_CATEGORIES.filter((c) => c.value).map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            {rootFolderForm.groupCategory === 'Personal' && (
              <div>
                <label className="block text-sm font-medium text-slate-700">Under (Personal) *</label>
                <select
                  value={rootFolderForm.groupSubCategory}
                  onChange={(e) => setRootFolderForm((f) => ({ ...f, groupSubCategory: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                >
                  {PERSONAL_SUBCATEGORIES.map((o) => (
                    <option key={o.value || 'blank'} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700">Folder type</label>
              <select
                value={rootFolderForm.folderTypeId}
                onChange={(e) => setRootFolderForm((f) => ({ ...f, folderTypeId: e.target.value }))}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
              >
                <option value="">—</option>
                {folderTypes?.map((ft) => (
                  <option key={ft._id ?? ft.id} value={ft._id ?? ft.id}>
                    {ft.name ?? ft.code ?? ft._id}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Folder name *</label>
              <input
                type="text"
                value={rootFolderForm.folderName}
                onChange={(e) => setRootFolderForm((f) => ({ ...f, folderName: e.target.value }))}
                placeholder="Enter folder name"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Folder number</label>
              <input
                type="text"
                value={rootFolderForm.folderNumber}
                onChange={(e) => setRootFolderForm((f) => ({ ...f, folderNumber: e.target.value }))}
                placeholder="Optional"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
              />
            </div>
          </div>
        </form>
        <div className="mt-4 flex justify-end gap-2">
          <ModalFormActions
            formId="root-folder-form"
            onCancel={() => { setRootFolderModal(false); setCreateFolderValidationError(''); }}
            isSubmitting={createFolderMutation.isPending}
            submitText="Create"
            submittingText="Creating…"
          />
        </div>
      </Modal>

      {/* New subfolder modal */}
      <Modal
        isOpen={!!subfolderModal}
        onClose={() => { setSubfolderModal(null); resetNewFolderForm(); }}
        title="New subfolder"
        size="sm"
      >
        <form
          id="subfolder-form"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!newFolderForm.folderName.trim() || !subfolderModal?.parentFolderId) return;
            const repository = treeFilters.repository || (subfolderModal?.repository ?? repositoryForCreate);
            if (!repository) return;
            let parentFolderId = subfolderModal.parentFolderId;
            const isParentRepository = repositoryIds?.includes(parentFolderId);
            if (isParentRepository && !newFolderForm.groupCategory) {
              setCreateFolderValidationError('Group category is required when creating under a repository.');
              return;
            }
            if (isParentRepository && newFolderForm.groupCategory === 'Personal' && !(newFolderForm.groupSubCategory || '').trim()) {
              setCreateFolderValidationError('Under (Personal) is required when category is Personal.');
              return;
            }
            setCreateFolderValidationError('');
            if (isParentRepository && newFolderForm.groupCategory) {
              try {
                parentFolderId = await ensureCategoryRootAndGetParentId(
                  repository,
                  newFolderForm.groupCategory,
                  newFolderForm.groupCategory === 'Personal' ? (newFolderForm.groupSubCategory || '').trim() : undefined,
                  parentFolderId
                );
              } catch (err) {
                setCreateFolderValidationError(err?.message ?? 'Failed to ensure category root.');
                return;
              }
            }
            const payload = {
              folderName: newFolderForm.folderName.trim(),
              folderNumber: newFolderForm.folderNumber.trim() || '',
              repository,
              parentFolderId,
              groupCategory: newFolderForm.groupCategory || '',
              folderTypeId: newFolderForm.folderTypeId || '',
            };
            if (newFolderForm.groupCategory === 'Personal') {
              payload.groupSubCategory = newFolderForm.groupSubCategory.trim() || '';
            }
            createFolderMutation.mutate(payload);
          }}
        >
          <p className="text-sm text-slate-600 mb-3">
            Create under: <strong>{subfolderModal?.parentTitle ?? '—'}</strong>
          </p>
          {(createFolderValidationError || createFolderMutation.isError) && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {createFolderValidationError ||
                createFolderMutation.error?.response?.data?.message ||
                createFolderMutation.error?.message ||
                'Failed to create folder.'}
            </div>
          )}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">Folder name *</label>
              <input
                type="text"
                value={newFolderForm.folderName}
                onChange={(e) => setNewFolderForm((f) => ({ ...f, folderName: e.target.value }))}
                placeholder="Enter folder name"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Folder number</label>
              <input
                type="text"
                value={newFolderForm.folderNumber}
                onChange={(e) => setNewFolderForm((f) => ({ ...f, folderNumber: e.target.value }))}
                placeholder="Optional"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
              />
            </div>
            {!treeFilters.repository && (
              <div>
                <label className="block text-sm font-medium text-slate-700">Repository *</label>
                <select
                  value={subfolderModal?.repository ?? repositoryForCreate}
                  onChange={(e) => setSubfolderModal((m) => (m ? { ...m, repository: e.target.value } : null))}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                >
                  <option value="">Select repository</option>
                  {repositories?.map((r) => (
                    <option key={r._id ?? r.name} value={r.name ?? ''}>
                      {r.name ?? r.pathSegment ?? r._id}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {treeFilters.repository && (
              <p className="text-sm text-slate-600">
                Repository: <strong>{treeFilters.repository}</strong>
              </p>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700">Group category</label>
              <select
                value={newFolderForm.groupCategory}
                onChange={(e) => setNewFolderForm((f) => ({ ...f, groupCategory: e.target.value }))}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
              >
                <option value="">—</option>
                {GROUP_CATEGORIES.filter((c) => c.value).map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            {newFolderForm.groupCategory === 'Personal' && (
              <div>
                <label className="block text-sm font-medium text-slate-700">Group sub-category</label>
                <input
                  type="text"
                  value={newFolderForm.groupSubCategory}
                  onChange={(e) => setNewFolderForm((f) => ({ ...f, groupSubCategory: e.target.value }))}
                  placeholder="e.g. Staff"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700">Folder type</label>
              <select
                value={newFolderForm.folderTypeId}
                onChange={(e) => setNewFolderForm((f) => ({ ...f, folderTypeId: e.target.value }))}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
              >
                <option value="">—</option>
                {folderTypes?.map((ft) => (
                  <option key={ft._id ?? ft.id} value={ft._id ?? ft.id}>
                    {ft.name ?? ft.code ?? ft._id}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </form>
        <div className="mt-4 flex justify-end gap-2">
          <ModalFormActions
            formId="subfolder-form"
            onCancel={() => { setSubfolderModal(null); resetNewFolderForm(); }}
            isSubmitting={createFolderMutation.isPending}
            submitText="Create"
            submittingText="Creating…"
          />
        </div>
      </Modal>

      {/* Upload document modal */}
      <Modal
        isOpen={!!uploadModal}
        onClose={() => { setUploadModal(null); setUploadFile(null); setUploadTitle(''); setUploadDocumentTypeId(''); }}
        title="Upload document"
        size="sm"
      >
        <form
          id="upload-document-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!uploadModal?.folderId || !uploadFile) return;
            const formData = new FormData();
            formData.append('file', uploadFile);
            formData.append('folderId', uploadModal.folderId);
            if (uploadTitle.trim()) formData.append('title', uploadTitle.trim());
            if (uploadDocumentTypeId) formData.append('documentTypeId', uploadDocumentTypeId);
            uploadDocMutation.mutate(formData);
          }}
        >
          <p className="text-sm text-slate-600 mb-3">
            Upload to folder: <strong>{uploadModal?.folderName ?? '—'}</strong>
          </p>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700">File *</label>
              <input
                type="file"
                onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
              />
              {uploadFile && (
                <p className="mt-1 text-xs text-slate-500">{uploadFile.name}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Title</label>
              <input
                type="text"
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                placeholder="Document title (optional)"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Document type</label>
              <select
                value={uploadDocumentTypeId}
                onChange={(e) => setUploadDocumentTypeId(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
              >
                <option value="">—</option>
                {documentTypes?.map((dt) => (
                  <option key={dt._id ?? dt.id} value={dt._id ?? dt.id}>
                    {dt.name ?? dt.code ?? dt._id}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </form>
        <div className="mt-4 flex justify-end gap-2">
          <ModalFormActions
            formId="upload-document-form"
            onCancel={() => { setUploadModal(null); setUploadFile(null); setUploadTitle(''); setUploadDocumentTypeId(''); }}
            isSubmitting={uploadDocMutation.isPending}
            submitText="Upload"
            submittingText="Uploading…"
            submitDisabled={!uploadFile}
          />
        </div>
      </Modal>

      {/* Rename folder modal */}
      <Modal
        isOpen={!!renameFolderModal}
        onClose={() => { setRenameFolderModal(null); setRenameValue(''); }}
        title="Rename folder"
        size="sm"
      >
        <form
          id="rename-folder-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!renameFolderModal?.id || !renameValue.trim()) return;
            updateFolderMutation.mutate({
              id: renameFolderModal.id,
              payload: { folderName: renameValue.trim() },
            });
          }}
        >
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Display name</label>
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="Folder name"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
              autoFocus
            />
          </div>
        </form>
        <div className="mt-4 flex justify-end gap-2">
          <ModalFormActions
            formId="rename-folder-form"
            onCancel={() => { setRenameFolderModal(null); setRenameValue(''); }}
            isSubmitting={updateFolderMutation.isPending}
            submitText="Rename"
            submittingText="Saving…"
          />
        </div>
      </Modal>

      {/* Edit document modal (title) */}
      <Modal
        isOpen={!!renameDocModal}
        onClose={() => { setRenameDocModal(null); setRenameValue(''); }}
        title="Edit document"
        size="sm"
      >
        <form
          id="rename-doc-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!renameDocModal?.id || !renameValue.trim()) return;
            updateDocMutation.mutate({
              id: renameDocModal.id,
              payload: { title: renameValue.trim() },
            });
          }}
        >
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Title</label>
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="Document title"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
              autoFocus
            />
          </div>
        </form>
        <div className="mt-4 flex justify-end gap-2">
          <ModalFormActions
            formId="rename-doc-form"
            onCancel={() => { setRenameDocModal(null); setRenameValue(''); }}
            isSubmitting={updateDocMutation.isPending}
            submitText="Save"
            submittingText="Saving…"
          />
        </div>
      </Modal>

      {/* Document viewer modal */}
      <Modal
        isOpen={!!documentViewer}
        onClose={() => setDocumentViewer(null)}
        title={documentViewer?.name ?? 'Document'}
        size="2xl"
        bodyClassName="p-0 flex flex-col min-h-[70vh]"
      >
        <div className="flex flex-1 flex-col min-h-0">
          {documentViewUrl ? (
            <>
              <div className="flex-1 min-h-0 rounded-b-lg overflow-hidden bg-slate-100">
                {(documentViewer?.mimetype ?? documentViewer?.mimeType ?? '').startsWith('image/') ? (
                  <img
                    src={documentViewUrl}
                    alt={documentViewer?.name ?? 'Document'}
                    className="w-full h-full object-contain"
                    style={{ minHeight: '60vh' }}
                  />
                ) : (
                  <iframe
                    title={documentViewer?.name ?? 'Document viewer'}
                    src={documentViewUrl}
                    className="w-full h-full min-h-[70vh] border-0"
                  />
                )}
              </div>
              <div className="flex items-center justify-end gap-2 p-3 border-t border-slate-200 bg-white">
                <a
                  href={documentViewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open in new tab
                </a>
                <button
                  type="button"
                  onClick={() => setDocumentViewer(null)}
                  className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700"
                >
                  <X className="h-4 w-4" />
                  Close
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center py-16 text-slate-500">
              Loading viewer…
            </div>
          )}
        </div>
      </Modal>

      {/* Delete confirmation (folder or document) */}
      <ConfirmDialog
        isOpen={!!deleteConfirm}
        title={deleteConfirm?.type === 'folder' ? 'Delete folder?' : 'Delete document?'}
        message={
          deleteConfirm
            ? `Are you sure you want to delete "${deleteConfirm.name}"? This action cannot be undone.`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteConfirm(null)}
        isLoading={deleteFolderMutation.isPending || deleteDocMutation.isPending}
      />

      {/* Folder created success */}
      <SuccessDialog
        isOpen={folderCreatedSuccess.isOpen}
        title="Success"
        message={folderCreatedSuccess.message}
        onClose={() => setFolderCreatedSuccess({ isOpen: false, message: '' })}
        variant="success"
      />

      {/* Document uploaded success */}
      <SuccessDialog
        isOpen={documentUploadedSuccess.isOpen}
        title="Success"
        message={documentUploadedSuccess.message}
        onClose={() => setDocumentUploadedSuccess({ isOpen: false, message: '' })}
        variant="success"
      />
    </>
  );
};

export default EdmsDashboard;

